import * as React from 'react';
import { HttpService } from '../../../Shared/HttpService';
import { IResponderFormulario } from '../interfaces/IResponderFormularioResponse';
import { ICargaResponderFormulario } from '../interfaces/ICargaResponderFormulario';
import { montarCargaParcialDeCabecalho, montarCargaParcialDeGrupoDePerguntas, montarCargaParcialDeRodape } from './helpers/montagemDeCarga';

const { createContext, useState, useEffect } = React;

interface IContext {
    responderFormulario: IResponderFormulario;
    setResponderFormulario: (responderFormulario: IResponderFormulario) => void;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    ultimaTentativaDeFinalizar: Date;
    setUltimaTentativaDeFinalizar: (ultimaTentativaDeFinalizar: Date) => void;
    salvarFormulario: () => void;
    finalizarFormulario: () => void;
    listaDeMensagensDeAlerta: string[];
    setListaDeMensagensDeAlerta: (listaDeMensagensDeAlerta: string[]) => void;
    mostrarTodosOsCollapses: boolean;
    setMostrarTodosOsCollapses: (mostrarTodosOsCollapses: boolean) => void;
    idsDosCollapsesQueVaoSerAbertos: Number[];
    setIdsDosCollapsesQueVaoSerAbertos: (idDosCollapsesQueVaoSerAbertos: Number[]) => void;
    responderFormularioOriginal: IResponderFormulario;
    salvarFormularioPosImportacao: (executarPosImportacao: () => void) => void;
}

const valorInicialResponderFormulario: IResponderFormulario = {
    id: 0,
    formularioId: 0,
    empresasid: 0,
    setoresDoUsuario: [],
    permitirCompartilhar: false,
    permitirEditarTitulo: false,
    permitirEditarCabecalhos: false,
    permitirEditarRespostas: false,
    permitirEditarAcoes: false,
    nome: '',
    titulo: '',
    hash: '',
    descricao: '',
    rascunho: true,
    cabecalhos: [],
    grupoDePerguntas: [],
    rodapes: [],
    formularioXEmpresaId: 0,
};

export const ResponderFormularioContext = createContext<IContext>({
    responderFormulario: valorInicialResponderFormulario,
    setResponderFormulario: () => { },
    isLoading: false,
    setIsLoading: () => { },
    ultimaTentativaDeFinalizar: new Date(),
    setUltimaTentativaDeFinalizar: () => { },
    salvarFormulario: () => { },
    finalizarFormulario: () => { },
    listaDeMensagensDeAlerta: [],
    setListaDeMensagensDeAlerta: () => { },
    mostrarTodosOsCollapses: false,
    setMostrarTodosOsCollapses: () => { },
    idsDosCollapsesQueVaoSerAbertos: [],
    setIdsDosCollapsesQueVaoSerAbertos: () => { },
    responderFormularioOriginal: valorInicialResponderFormulario,
    salvarFormularioPosImportacao: () => { },
});

export const ResponderFormularioContextProvider = ({ children, params }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [listaDeMensagensDeAlerta, setListaDeMensagensDeAlerta] = useState<string[]>([]);
    const [responderFormulario, setResponderFormulario] = useState<IResponderFormulario>(valorInicialResponderFormulario);
    const [ultimaTentativaDeFinalizar, setUltimaTentativaDeFinalizar] = useState<Date>(new Date());
    const [mostrarTodosOsCollapses, setMostrarTodosOsCollapses] = useState(false);
    const [idsDosCollapsesQueVaoSerAbertos, setIdsDosCollapsesQueVaoSerAbertos] = useState<Number[]>([]);
    const [responderFormularioOriginal, setResponderFormularioOriginal] = useState<IResponderFormulario>(valorInicialResponderFormulario);
    const auth = localStorage.getItem('auth');
    const tipoDeConta = auth ? JSON.parse(auth).tipoDeConta : 0;


    const montarCargaParaApi = (): ICargaResponderFormulario => {
        const { id, formularioId, hash, rascunho, titulo, cabecalhos, grupoDePerguntas, rodapes, empresasid } = responderFormulario;
        const camposAlterados: string[] = [];
        if (titulo !== responderFormularioOriginal.titulo) camposAlterados.push('titulo');
        if (rascunho !== responderFormularioOriginal.rascunho) camposAlterados.push('rascunho');
        
        return {
            id,
            formularioId,
            empresasid,
            hash,
            rascunho,
            titulo,
            atualizacaoParcial: true,
            camposAlterados,
            cabecalhos: montarCargaParcialDeCabecalho(cabecalhos, responderFormularioOriginal.cabecalhos),
            grupoDePerguntas: montarCargaParcialDeGrupoDePerguntas(grupoDePerguntas, responderFormularioOriginal.grupoDePerguntas),
            rodapes: montarCargaParcialDeRodape(rodapes, responderFormularioOriginal.rodapes),
        }
    }

    const salvarFormularioPosImportacao = (executarPosImportacao) => {
        setIsLoading(_state => true);

        HttpService.fetch('api/ResultadoNovoApi/ResponderFormulario', {
            method: 'POST',
            body: JSON.stringify(montarCargaParaApi()),
        })
            .then(response => {
                return new Promise<any>((resolve) => response.json()
                    .then((json) => resolve({
                        status: response.status,
                        ok: response.ok,
                        json,
                    })));
            })
            .then((data) => {
                if (data.status === 200) {
                    // A importação não recarrega a tela. Atualizar a referência
                    // impede que um próximo salvamento reenvie alterações já salvas.
                    setResponderFormularioOriginal(JSON.parse(JSON.stringify(responderFormulario)));
                    setListaDeMensagensDeAlerta(['salvoComSucesso']);
                    executarPosImportacao();
                } else {
                    setListaDeMensagensDeAlerta(['ocorreuUmErroAoSalvar']);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setIsLoading(_state => false);
            })
            .catch((error) => {
                setIsLoading(_state => false);
                console.log("Erro ao salvar o formulário: ", error)
            });
    };

    const salvarFormulario = (finalizando?: string) => {
        setIsLoading(_state => true);
        
        HttpService.fetch('api/ResultadoNovoApi/ResponderFormulario', {
            method: 'POST',
            body: JSON.stringify(montarCargaParaApi()),
        })
            .then(response => {
                return new Promise<any>((resolve) => response.json()
                    .then((json) => resolve({
                        status: response.status,
                        ok: response.ok,
                        json,
                    })));
            })
            .then((data) => {
                if (data.status === 200) {
                    if (finalizando === 'finalizando') return window.location.href = tipoDeConta === 1 ? `/marketPlace/detail/${responderFormulario.formularioId}` : `/`;
                    setListaDeMensagensDeAlerta(['salvoComSucesso']);
                    window.location.reload();
                } else {
                    setListaDeMensagensDeAlerta(['ocorreuUmErroAoSalvar']);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setIsLoading(_state => false);
            })
            .catch((error) => {
                setIsLoading(_state => false);
                console.log("Erro ao salvar o formulário: ", error)
            });
    }

    const finalizarFormulario = () => {
        setResponderFormulario(() => ({ ...responderFormulario, rascunho: false }));
    };

    const verificarSetoresDoUsuario = async () => {
        try {
            const response = await HttpService.fetch('api/SetorApi/ListSetoresDoUsuario', { method: 'GET' });
            if (response.status === 200) {
                return await response.json();
            }
            return null;
        } catch (error) {
            console.log("Erro ao verificar setores do usuário: ", error);
            return null;
        }
    };

    const fetchFormulario = async () => {
        try {
            setIsLoading(true);
    
            const response = await HttpService.fetch(`api/FormularioIniciadoApi/ObterFormulario/${params.hash}`, { method: 'GET' });
            const data = await response.json();
            
            const setoresDoUsuario = await verificarSetoresDoUsuario();

            const dataComSetores = {
                ...data,
                setoresDoUsuario: setoresDoUsuario ?? []
            };

            setResponderFormulario(dataComSetores);
            setResponderFormularioOriginal(JSON.parse(JSON.stringify(dataComSetores)));
        } catch (error) {
            console.log("Erro ao obter o formulário: ", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        setIsLoading(_state => true);
        fetchFormulario();
    }, []);

    useEffect(() => {
        if (responderFormulario.rascunho === false) {
            salvarFormulario('finalizando');
        }
    }, [responderFormulario.rascunho]);

    return (
        <ResponderFormularioContext.Provider value={{
            responderFormulario,
            setResponderFormulario,
            isLoading,
            setIsLoading,
            ultimaTentativaDeFinalizar,
            setUltimaTentativaDeFinalizar,
            salvarFormulario,
            finalizarFormulario,
            listaDeMensagensDeAlerta,
            setListaDeMensagensDeAlerta,
            mostrarTodosOsCollapses,
            setMostrarTodosOsCollapses,
            idsDosCollapsesQueVaoSerAbertos,
            setIdsDosCollapsesQueVaoSerAbertos,
            responderFormularioOriginal,
            salvarFormularioPosImportacao,
        }}>
            {children}
        </ResponderFormularioContext.Provider>
    );
};
